import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import MfaEnrollmentPanel from './MfaEnrollmentPanel';
import { unenrollFactor, friendlyAuthError } from '../../services/careAuthService';
import './Security.css';

function factorLabel(f) {
  if (f.factorId === 'phone') return { title: f.displayName || 'Text message', sub: f.phoneNumber ? `Phone ending ${String(f.phoneNumber).slice(-4)}` : 'SMS' };
  if (f.factorId === 'totp') return { title: f.displayName || 'Authenticator app', sub: 'Time-based codes' };
  return { title: f.displayName || f.factorId, sub: '' };
}

/** Header "Two-step verification" modal: view, add, and remove factors. */
export default function MfaSettingsModal({ open, onClose }) {
  const { mfaFactors, isMicrosoftUser, refreshSession, mfaSatisfied } = useAuth();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  if (!open) return null;

  const handleRemove = async (f) => {
    if (mfaFactors.length <= 1 && mfaSatisfied) {
      // Removing the last factor would lock the account out at the next sign-in once enforcement is on.
      const ok = window.confirm('This is your only verification method. Removing it means you will have to set one up again at your next sign-in. Continue?');
      if (!ok) return;
    }
    setError('');
    setBusy(true);
    try {
      await unenrollFactor(f);
      await refreshSession();
      setNotice('Verification method removed.');
    } catch (err) {
      setError(friendlyAuthError(err, 'Could not remove that method.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sec-modal-overlay" onClick={onClose} role="presentation">
      <div className="sec-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="mfa-settings-title">
        <div className="sec-modal-header">
          <h2 id="mfa-settings-title">Two-step verification</h2>
          <button type="button" className="sec-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {isMicrosoftUser ? (
          <p className="sec-desc">
            Your sign-in is protected by your Microsoft work account and Duo. Manage verification methods in
            your Microsoft security settings — nothing to configure here.
          </p>
        ) : adding ? (
          <MfaEnrollmentPanel
            onEnrolled={async () => {
              await refreshSession();
              setAdding(false);
              setNotice('Verification method added.');
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <>
            {error && <div className="sec-error" role="alert">{error}</div>}
            {notice && <div className="sec-success" role="status">{notice}</div>}
            {mfaFactors.length === 0 ? (
              <p className="sec-desc">No verification method is set up yet.</p>
            ) : (
              <ul className="sec-factors">
                {mfaFactors.map((f) => {
                  const { title, sub } = factorLabel(f);
                  return (
                    <li key={f.uid} className="sec-factor">
                      <div>
                        {title}
                        {sub && <small>{sub}</small>}
                      </div>
                      <button type="button" className="sec-btn sec-btn--danger" onClick={() => handleRemove(f)} disabled={busy}>
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="sec-actions">
              <button type="button" className="sec-btn sec-btn--primary" onClick={() => { setNotice(''); setError(''); setAdding(true); }} disabled={busy || mfaFactors.length >= 5}>
                Add a method
              </button>
              <button type="button" className="sec-btn sec-btn--secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
